import React, { useState } from 'react';
import './ToolPage.css';

const ColorPicker = () => {
  const [hex, setHex] = useState('#000000');
  const [rgb, setRgb] = useState({ r: 0, g: 0, b: 0 });
  const [hsl, setHsl] = useState({ h: 0, s: 0, l: 0 });

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHex = (r, g, b) => {
    return '#' + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('').toUpperCase();
  };

  const rgbToHsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  const updateFromHex = (hexValue) => {
    setHex(hexValue);
    const rgbValue = hexToRgb(hexValue);
    if (rgbValue) {
      setRgb(rgbValue);
      setHsl(rgbToHsl(rgbValue.r, rgbValue.g, rgbValue.b));
    }
  };

  const updateFromRgb = (r, g, b) => {
    const newRgb = { r, g, b };
    setRgb(newRgb);
    setHex(rgbToHex(r, g, b));
    setHsl(rgbToHsl(r, g, b));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="tool-page">
      <div className="tool-header">
        <h1 className="tool-title">Color Picker</h1>
        <p className="tool-description">
          Pick colors and convert between HEX, RGB, and HSL formats
        </p>
      </div>

      <div className="tool-content">
        <div className="color-picker-container">
          <div className="color-preview-section">
            <div
              className="color-preview"
              style={{ backgroundColor: hex }}
            />
            <input
              type="color"
              value={hex}
              onChange={(e) => updateFromHex(e.target.value)}
              className="color-input-native"
            />
          </div>

          <div className="color-values">
            <div className="color-value-group">
              <label className="input-label">HEX</label>
              <div className="value-with-copy">
                <input
                  type="text"
                  className="input-field"
                  value={hex}
                  onChange={(e) => updateFromHex(e.target.value)}
                />
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(hex)}
                >
                  Copy
                </button>
              </div>
            </div>

            <div className="color-value-group">
              <label className="input-label">RGB</label>
              <div className="rgb-inputs">
                <input
                  type="number"
                  className="input-field small"
                  value={rgb.r}
                  min={0}
                  max={255}
                  onChange={(e) => updateFromRgb(Number(e.target.value), rgb.g, rgb.b)}
                />
                <input
                  type="number"
                  className="input-field small"
                  value={rgb.g}
                  min={0}
                  max={255}
                  onChange={(e) => updateFromRgb(rgb.r, Number(e.target.value), rgb.b)}
                />
                <input
                  type="number"
                  className="input-field small"
                  value={rgb.b}
                  min={0}
                  max={255}
                  onChange={(e) => updateFromRgb(rgb.r, rgb.g, Number(e.target.value))}
                />
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`)}
                >
                  Copy
                </button>
              </div>
            </div>

            <div className="color-value-group">
              <label className="input-label">HSL</label>
              <div className="value-with-copy">
                <input
                  type="text"
                  className="input-field"
                  value={`hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`}
                  readOnly
                />
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(`hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`)}
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;
